import type { ConditionalAnswer } from '../domain/types.js';
import type { ActorContext, HistoryEventSlice, PromptSourceBundle } from './types.js';
import { ACCEPTED_ANSWER_STATUSES } from './types.js';
import { privacySafeName, sanitizeDisplayName } from './format.js';

/** Exclude stale and unanswered answers from prompt context. */
export function acceptedAnswers(
  answers: Record<string, ConditionalAnswer>,
): Record<string, ConditionalAnswer> {
  return Object.fromEntries(
    Object.entries(answers).filter(([, a]) => ACCEPTED_ANSWER_STATUSES.has(a.status)),
  );
}

/** Apply privacy-safe display names before any compiled actor output. */
export function normalizeActor(actor: ActorContext): ActorContext {
  const displayName = actor.firstName
    ? privacySafeName(actor.firstName, actor.lastInitial)
    : sanitizeDisplayName(actor.displayName);
  return { ...actor, displayName };
}

export function normalizeActors(all: ActorContext[]): ActorContext[] {
  return all.map(normalizeActor);
}

export function filterActors(
  all: ActorContext[],
  includeIds: string[],
): ActorContext[] {
  const set = new Set(includeIds);
  return normalizeActors(all.filter((a) => set.has(a.actorId)));
}

export function filterEventsForActors(
  events: HistoryEventSlice[],
  actorIds: string[],
  maxRecent: number,
): HistoryEventSlice[] {
  const set = new Set(actorIds);
  return events
    .filter((e) => e.actorIds.some((id) => set.has(id)))
    .slice(-maxRecent);
}

export function filterActiveDevices(source: PromptSourceBundle): string[] {
  return source.activeDevices.map((d) => d.deviceId);
}

export function buildWorldContext(source: PromptSourceBundle): {
  worldId: string;
  worldVersion: number;
  locationType: PromptSourceBundle['locationType'];
  location: PromptSourceBundle['location'];
  connected: PromptSourceBundle['connected'];
  answers: Record<string, ConditionalAnswer>;
  timePeriod: PromptSourceBundle['timePeriod'];
  schedule: PromptSourceBundle['schedule'];
  assumptions: PromptSourceBundle['assumptions'];
} {
  return {
    worldId: source.worldId,
    worldVersion: source.worldVersion,
    locationType: source.locationType,
    location: source.location,
    connected: source.connected ?? null,
    answers: acceptedAnswers(source.answers),
    timePeriod: source.timePeriod,
    schedule: source.schedule,
    assumptions: source.assumptions.filter((a) => a.accepted),
  };
}
